import { useState, useEffect, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig, AnimatePresence } from "framer-motion";
import { ThemeProvider } from "./components/ThemeProvider";
const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const Courses = lazy(() => import("./pages/Courses"));
const Internship = lazy(() => import("./pages/Internship"));
const WebDeveloperInternship = lazy(() => import("./pages/WebDeveloperInternship"));
const Workshops = lazy(() => import("./pages/Workshops"));
const WorkshopDetail = lazy(() => import("./pages/WorkshopDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Careers = lazy(() => import("./pages/Careers"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CookiesPolicy = lazy(() => import("./pages/CookiesPolicy"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const StudyMaterials = lazy(() => import("./pages/StudyMaterials"));
const MaterialDetail = lazy(() => import("./pages/MaterialDetail"));
import { CustomScrollbar } from "@/components/ui/custom-scrollbar";
import ScrollToTop from "@/components/ScrollToTop";
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
import ProtectedRoute from "@/components/ProtectedRoute";
const Admin = lazy(() => import("./admin/Admin"));
const Users = lazy(() => import("./admin/Users"));
const UserView = lazy(() => import("./admin/UserView"));
const LeadView = lazy(() => import("./admin/LeadView"));
const EnrollmentView = lazy(() => import("./admin/EnrollmentView"));
const ContactView = lazy(() => import("./admin/ContactView"));
const ApplicationView = lazy(() => import("./admin/ApplicationView"));
const WorkshopApplicationView = lazy(() => import("./admin/WorkshopApplicationView"));
const WorkshopConsultationView = lazy(() => import("./admin/WorkshopConsultationView"));
const Blogs = lazy(() => import("./admin/Blogs"));
const Materials = lazy(() => import("./admin/Materials"));
const CreateBlog = lazy(() => import("./admin/CreateBlog"));
const CodoChatWidget = lazy(() => import("./components/CodoChatWidget"));
const Scene3D = lazy(() => import("./components/shared/Scene3D"));
const InquiryModal = lazy(() => import("./components/InquiryModal"));
const FloatingCTAs = lazy(() => import("./components/FloatingCTAs"));
import { Loader2 } from "lucide-react";

const queryClient = new QueryClient();

const AppLayout = ({ 
  children, 
  isInquiryOpen, 
  setIsInquiryOpen 
}: { 
  children: React.ReactNode,
  isInquiryOpen: boolean,
  setIsInquiryOpen: (open: boolean) => void
}) => {
  const location = useLocation();
  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/blogs") ||
    location.pathname.startsWith("/users") ||
    location.pathname === "/login";

  return (
    <div className="flex min-h-screen flex-col">
      {!isAdminRoute && <Navbar />}
      <main className={`flex-1 ${!isAdminRoute ? 'pt-20 md:pt-24' : ''}`}>{children}</main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && (
        <Suspense fallback={null}>
          <CodoChatWidget />
          <InquiryModal open={isInquiryOpen} onOpenChange={setIsInquiryOpen} />
          <FloatingCTAs />
        </Suspense>
      )}
    </div>
  );
};

const App = () => {
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [showScene3D, setShowScene3D] = useState(false);

  useEffect(() => {
    const handleOpenInquiry = () => setIsInquiryOpen(true);
    window.addEventListener("open-inquiry", handleOpenInquiry);
    
    // Delay Scene3D loading to improve LCP and TBT
    const timer = setTimeout(() => {
      setShowScene3D(true);
    }, 4000);
    
    return () => {
      window.removeEventListener("open-inquiry", handleOpenInquiry);
      clearTimeout(timer);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider key="content" attribute="class" defaultTheme="light" enableSystem>
        {showScene3D && (
          <Suspense fallback={null}>
            <Scene3D />
          </Suspense>
        )}
        <Toaster />
            <Sonner />
      <div className="min-h-screen">
        <TooltipProvider>
          <BrowserRouter>
            <ScrollToTop behavior="smooth">
              <MotionConfig>
                <AppLayout isInquiryOpen={isInquiryOpen} setIsInquiryOpen={setIsInquiryOpen}>
                  <Suspense fallback={
                      <div className="flex min-h-[70vh] w-full items-center justify-center">
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                      </div>
                    }>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/courses" element={<Courses />} />
                        <Route path="/internship" element={<Internship />} />
                        <Route path="/internship/web-developer" element={<WebDeveloperInternship />} />
                        <Route path="/workshops" element={<Workshops />} />
                        <Route path="/workshops/:slug"
                          element={<WorkshopDetail />}

                        />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogDetail />} />
                        <Route path="/careers" element={<Careers />} />
                        <Route path="/careers/:slug" element={<JobDetail />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/courses/:slug" element={<CourseDetail />} />
                        <Route path="/study-materials" element={<StudyMaterials />} />
                        <Route path="/study-materials/:id" element={<MaterialDetail />} />
                        <Route path="/terms-of-use" element={<TermsOfUse />} />
                        <Route
                          path="/privacy-policy"
                          element={<PrivacyPolicy />}
                        />
                        <Route path="/refund-policy" element={<RefundPolicy />} />
                        <Route
                          path="/cookies-policy"
                          element={<CookiesPolicy />}
                        />
                        <Route
                          path="/shipping-policy"
                          element={<ShippingPolicy />}
                        />
                        <Route path="/login" element={<AdminLogin />} />
                        <Route path="*" element={<NotFound />} />
                        <Route
                          path="/admin"
                          element={
                            <ProtectedRoute>
                              <Admin />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/blogs"
                          element={
                            <ProtectedRoute>
                              <Blogs />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/materials"
                          element={
                            <ProtectedRoute>
                              <Materials />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/blogs/create"
                          element={
                            <ProtectedRoute>
                              <CreateBlog />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/blogs/:blogId"
                          element={
                            <ProtectedRoute>
                              <CreateBlog />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/users"
                          element={
                            <ProtectedRoute>
                              <Users />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/users/:id"
                          element={
                            <ProtectedRoute>
                              <UserView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/leads/:id"
                          element={
                            <ProtectedRoute>
                              <LeadView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/enrollments/:id"
                          element={
                            <ProtectedRoute>
                              <EnrollmentView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/contact/:id"
                          element={
                            <ProtectedRoute>
                              <ContactView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/applications/:id"
                          element={
                            <ProtectedRoute>
                              <ApplicationView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/workshop-applications/:id"
                          element={
                            <ProtectedRoute>
                              <WorkshopApplicationView />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="admin/workshop-consultations/:id"
                          element={
                            <ProtectedRoute>
                              <WorkshopConsultationView />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Suspense>
                </AppLayout>
              </MotionConfig>
            </ScrollToTop>
          </BrowserRouter>
        </TooltipProvider>
      </div>
    </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
